import { useEffect, useState } from "react";

/* ------------------------------------------------------------------------
 * Independence Day date gate — hero banner only.
 *
 * The date is resolved in Asia/Kolkata regardless of where the visitor is, so
 * someone in Dubai or New Jersey sees the banner on the same calendar day as
 * customers in India. Live for 15 August only: it appears at 00:00 IST on the
 * 15th and reverts at 00:00 IST on the 16th, with no redeploy or page reload.
 *
 *   ?theme=id   force the banner on,  any day  (previewing)
 *   ?theme=off  force the banner off, any day  (suppression)
 *
 * This gate adds no global class and touches no other page. Swapping the hero
 * is the only thing it does.
 * ---------------------------------------------------------------------- */

const IST = "Asia/Kolkata";
 
/* Live window, inclusive of both ends and evaluated in IST.
 *
 * 14 -> 16 means the theme appears at 00:00 IST on 14 August and disappears
 * at 00:00 IST on 17 August, i.e. it runs for the whole of the 14th, 15th and
 * 16th. Set START and END to the same number for a single day. */
const FESTIVAL_MONTH = 8;
const FESTIVAL_START_DAY = 14;
const FESTIVAL_END_DAY = 16;

/** Reads ?theme=. Returns true, false, or null when there is no override. */
export function readThemeOverride(search) {
    const query =
        search !== undefined ? search : typeof window !== "undefined" ? window.location.search : "";
    if (!query) return null;

    const value = new URLSearchParams(query).get("theme");
    if (value === "id") return true;
    if (value === "off") return false;
    return null;
}

/** True when the festive banner should be showing right now. */
export function isIndependenceDay(date = new Date()) {
    const override = readThemeOverride();
    if (override !== null) return override;

    const p = new Intl.DateTimeFormat("en-GB", {
        timeZone: IST,
        month: "2-digit",
        day: "2-digit",
    })
        .formatToParts(date)
        .reduce((acc, part) => {
            acc[part.type] = part.value;
            return acc;
        }, {});

    return (
        +p.month === FESTIVAL_MONTH &&
        +p.day >= FESTIVAL_START_DAY &&
        +p.day <= FESTIVAL_END_DAY
    );
}

/** Milliseconds from `date` until the next 00:00 IST, plus a 1s cushion. */
export function msUntilNextIstMidnight(date = new Date()) {
    const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: IST,
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    })
        .format(date)
        .split(":")
        .map(Number);

    // Some engines render midnight as 24:00:00 rather than 00:00:00.
    const elapsed = (parts[0] % 24) * 3600 + parts[1] * 60 + parts[2];
    return (86400 - elapsed) * 1000 + 1000;
}

/**
 * Returns whether the festive banner is live, re-checking itself at the next
 * IST midnight so a tab left open across the 15th flips back on its own.
 */
export function useIndependenceDayTheme() {
    const [festive, setFestive] = useState(() => isIndependenceDay());

    useEffect(() => {
        let timer = null;
        let cancelled = false;

        const evaluate = () => {
            if (cancelled) return;
            setFestive(isIndependenceDay());
            timer = window.setTimeout(evaluate, msUntilNextIstMidnight());
        };

        evaluate();

        return () => {
            cancelled = true;
            if (timer !== null) window.clearTimeout(timer);
        };
    }, []);

    return festive;
}
